import { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/apiService';
import useAutoCache from '../../hooks/useAutoCache';

// #528 (Scheibe 3 aus #313): Mails im Dashboard -- Liste aus dem Auto-Cache,
// Sofort-Aktualisierung per SSE bei neuer Mail, Neuladen nach "gelesen" und
// die entprellte Volltextsuche. Code unveraendert aus Dashboard.js
// uebernommen, Muster aus #312/#471: Zustand im Hook, das Dashboard ruft ihn
// auf. So uebersteht das SSE-Abo das fruehe `return` beim Laden und
// "Erneut versuchen" (Dashboard.scheiben.test.js).
//
// setEmailsLoading geht mit hinaus: loadDashboardData und "Erneut versuchen"
// im Dashboard setzen den Lade-Hinweis der Spalte selbst.
export default function useDashboardMails() {

  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(true);
  // Email Volltextsuche
  const [emailSearchFilter, setEmailSearchFilter] = useState('');
  const [emailSearchResults, setEmailSearchResults] = useState([]);
  const [emailSearchLoading, setEmailSearchLoading] = useState(false);

  // Email auto-cache with callback to update state
  const handleEmailUpdate = useCallback((newEmails) => {
    // Show the last 30 emails (already limited by server)
    setEmails(newEmails);
    // Erste Daten für Mails eingetroffen → Sektion ist benutzbar
    setEmailsLoading(false);
  }, []);

  // Setup automatic cache management
  const { manualRefresh: manualRefreshEmails, errorCount: emailErrorCount, isSuspended: emailsSuspended, status: emailsStatus } = useAutoCache({
    type: 'emails',
    enabled: true,
    onDataUpdate: handleEmailUpdate,
    checkInterval: 60000 // Check every 60 seconds
  });

  // SSE: subscribe to email push and trigger immediate refresh on new mail
  useEffect(() => {
    let es;
    try {
      es = apiService.subscribeEmailsPush({
        onEvent: async (name, data) => {
          if (name === 'email_created') {
            try {
              await manualRefreshEmails();
              // leichte Verzögerung, falls Graph-Indexierung ein paar ms hinterher ist
              setTimeout(() => {
                try { manualRefreshEmails(); } catch (_) {}
              }, 500);
            } catch (e) {
              console.warn('SSE refresh emails failed:', e?.message || e);
            }
          }
        }
      });
    } catch (_) {}
    return () => {
      try { es && es.close && es.close(); } catch (_) {}
    };
  }, [manualRefreshEmails]);

  // Handle email read status updates
  const handleEmailReadUpdate = useCallback(async () => {
    try {
      await manualRefreshEmails();
      // Trigger re-render to update linked items
      setEmails(prevEmails => [...prevEmails]);
    } catch (error) {
      console.error('Error refreshing emails after read status change:', error);
    }
  }, [manualRefreshEmails]);

  // E-Mail Volltextsuche (debounced)
  useEffect(() => {
    let timerId = null;

    const runSearch = async () => {
      const query = emailSearchFilter.trim();
      if (!query) {
        setEmailSearchResults([]);
        setEmailSearchLoading(false);
        return;
      }
      try {
        setEmailSearchLoading(true);
        const response = await apiService.searchEmails(query, 200);
        setEmailSearchResults(response.value || []);
      } catch (e) {
        console.error('Email search failed:', e);
        setEmailSearchResults([]);
      } finally {
        setEmailSearchLoading(false);
      }
    };

    // debounce 300ms
    timerId = setTimeout(runSearch, 300);
    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [emailSearchFilter]);

  return {
    emails, emailsLoading, setEmailsLoading, manualRefreshEmails, emailErrorCount, emailsSuspended, emailsStatus,
    emailSearchFilter, setEmailSearchFilter, emailSearchResults, emailSearchLoading, handleEmailReadUpdate,
  };
}
